import React from "react";
import { Edit, useForm } from "@refinedev/antd";
import { useNavigate } from "react-router-dom";
import { useShow } from "@refinedev/core";

import { 
  Form,
  Input,
  DatePicker,
  Select,
  Checkbox,
  Radio,
  Card,
  Typography,
  Row,
  Col,
  Spin,
  Button,
  Space,
} from "antd";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { IRaceMaster } from "@/interfaces/race.interface";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { formatDate } from "@/utils/format";

const { Title, Text } = Typography;

interface IRaceMasterForm extends IRaceMaster {
  race_type: 'normal' | 'dragon';
}

export const RaceEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  // データ取得
  const { queryResult } = useShow<IRaceMaster>({
    resource: "race_master",
    id: id,
  });
  const { data, isLoading } = queryResult;
  const record = data?.data;

  // フォーム設定
  const { formProps, saveButtonProps, onFinish } = useForm<IRaceMasterForm>({
    meta: {
      redirect: false,
      onMutationSuccess: () => {
        window.location.href = '/races';
      },
    },
    resource: "race_master",
    id,
    action: "edit",
    redirect: false,
    successNotification: {
      message: "レースを更新しました。",
      type: "success",
    },
    errorNotification: {
      message: "レースの更新に失敗しました。",
      type: "error",
    },
    queryOptions: {
      enabled: !!id,
    },
    onMutationSuccess: () => {
      navigate(`/races/show/${id}`);
    },
  });

  const handleSubmit = async (values: any) => {
    const { race_type, race_date, deadline_date, publication_date_time, ...rest } = values as IRaceMasterForm;
    
    const formattedValues = {
      ...rest,
      race_date: race_date ? dayjs(race_date).add(9, 'hour').format('YYYY-MM-DD') : undefined,
      deadline_date: deadline_date ? dayjs(deadline_date).add(9, 'hour').format('YYYY-MM-DD') : undefined,
      publication_date_time: publication_date_time ? dayjs(publication_date_time).subtract(9, 'hour').format('YYYY-MM-DD HH:mm:00') : undefined, // UTC+9（日本時間）
      is_point_race: false,
      is_dragon: race_type === 'dragon',
    };
    
    await onFinish(formattedValues);
  };

  // 初期値をフォームに設定
  React.useEffect(() => {
    if (record) {
      const raceType = record.is_dragon ? 'dragon' : 'normal';
      
      formProps.form?.setFieldsValue({
        name: record.name,
        race_date: record.race_date ? dayjs(record.race_date) : undefined,
        deadline_date: record.deadline_date ? dayjs(record.deadline_date) : undefined,
        publication_date_time: record.publication_date_time ? dayjs(record.publication_date_time) : undefined,
        publication_status: record.publication_status || "draft",
        race_type: raceType,
        is_multiple: record.is_multiple || false,
      });
    }
  }, [record]);

  if (isLoading) {
    return (
      <div className="p-4">
        <Spin tip="読み込み中...">
          <div className="p-24" />
        </Spin>
      </div>
    );
  }

  return (
    <Edit
      headerButtons={[]}
      saveButtonProps={{ style: { display: 'none' } }}
    >
      <Card>
        <div className="space-y-6">
          <div className="space-y-4">
            <Title level={2}>レース編集</Title>
            <Text type="secondary">レースの詳細情報を編集してください</Text>
          </div>

          <Form
            {...formProps}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item
                  label="レース名"
                  name="name"
                  rules={[{ required: true, message: 'レース名を入力してください' }]}
                >
                  <Input placeholder="レース名を入力" />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Card size="small" className="bg-gray-50">
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="race_type"
                        label="レースタイプ"
                      >
                        <Radio.Group>
                          <Radio value="normal">通常レース</Radio>
                          <Radio value="dragon">ドラゴンレース</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        name="is_multiple"
                        valuePropName="checked"
                        label="開催形式"
                      >
                        <Checkbox>複数日開催</Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="開催日"
                  name="race_date"
                  rules={[{ required: true, message: '開催日を選択してください' }]}
                  getValueProps={(value) => ({
                    value: value ? dayjs(value) : undefined,
                  })}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="日付を選択"
                    format="YYYY年MM月DD日"
                    showToday
                    getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="エントリー締切日"
                  name="deadline_date"
                  rules={[{ required: true, message: '締切日を選択してください' }]}
                  getValueProps={(value) => ({
                    value: value ? dayjs(value) : undefined,
                  })}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="日付を選択"
                    format="YYYY年MM月DD日"
                    showToday
                    getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  label="公開日時"
                  name="publication_date_time"
                  rules={[{ required: true, message: '公開日時を選択してください' }]}
                  getValueProps={(value) => ({
                    value: value ? dayjs(value) : undefined,
                  })}
                >
                  <DatePicker
                    showTime
                    style={{ width: '100%' }}
                    placeholder="日時を選択"
                    format="YYYY-MM-DD HH:mm"
                    showToday
                    getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  label="公開ステータス"
                  name="publication_status"
                >
                  <Select>
                    <Select.Option value="draft">下書き</Select.Option>
                    <Select.Option value="published">公開中</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row justify="end" style={{ marginTop: 24 }}>
              <Space>
                <Button
                  icon={<CloseOutlined />}
                  onClick={() => navigate(`/races/show/${id}`)}
                >
                  キャンセル
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  {...saveButtonProps}
                >
                  保存
                </Button>
              </Space>
            </Row>
          </Form>
        </div>
      </Card>
    </Edit>
  );
};